import {
  REWARD_TYPES,
  REWARD_CARD_TYPES,
  REWARD_STATUS,
  isInventoryManagedRewardType
} from '@/utils/constants/rewardsConstants';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {
  getProductsToMap,
  getProductImageUrls
} from '@/utils/rewardPayloadUtils';

/**
 * Reward type to validator key mapping - reduces cognitive complexity
 */
export const REWARD_TYPE_VALIDATOR_MAP = {
  [REWARD_TYPES.BONUS_PRODUCTS]: 'BonusProducts',
  [REWARD_TYPES.VISA_CARD]: 'VisaCard',
  [REWARD_TYPES.PEP_VOUCHER_PRODUCT]: 'PepVoucherProduct',
  [REWARD_TYPES.PRODUCT]: 'PepVoucherProduct',
  [REWARD_TYPES.CASH]: 'CashDiscount',
  [REWARD_TYPES.DISCOUNT]: 'CashDiscount',
  [REWARD_TYPES.PEP_VOUCHER_DISCOUNT]: 'CashDiscount',
  [REWARD_TYPES.DIGITAL]: 'DigitalCode',
  [REWARD_TYPES.DIGITAL_PRE_ORDER]: 'DigitalCode',
  [REWARD_TYPES.LOYALTY]: 'Loyalty'
};

/**
 * Maps reward type for API payload
 * @param {string} rt - Reward type
 * @returns {string} Mapped reward type
 */
export const mapRewardType = (rt) => {
  if (!rt) return '';
  if (rt === REWARD_TYPES.CASH) return 'Cash';
  return rt;
};

/**
 * Converts date to ISO string format
 * @param {Date|string} d - Date to convert
 * @returns {string|undefined} ISO string or undefined
 */
export const toIso = (d) => {
  try {
    if (!d) return undefined;
    const date = typeof d === 'string' ? new Date(d) : d;
    return isNaN(date?.getTime?.()) ? undefined : date.toISOString();
  } catch {
    return undefined;
  }
};

/**
 * Sanitizes rewardText before sending to API (remove client-only flags).
 * When languages are provided, backfills missing language codes as null.
 * @param {Object} rewardText - Reward text object
 * @param {Array} [languages] - Optional array of language objects ({code}) or codes
 * @returns {Object} Sanitized reward text
 */
export const sanitizeRewardTextForPayload = (rewardText, languages) => {
  if (!rewardText || typeof rewardText !== 'object') return rewardText;
  const cleaned = {...rewardText};
  delete cleaned.enAutofillDisabled;
  if (Array.isArray(languages) && languages.length > 0) {
    languages.forEach((lang) => {
      const code = lang.code || lang;
      if (!(code in cleaned)) {
        cleaned[code] = null;
      }
    });
  }
  return cleaned;
};

const hasNonEmptyValue = (value) => {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
};

const buildBonusProductsQuotaFields = (quota) => {
  const isUnlimitedQuota = quota?.unlimited === true;
  if (isUnlimitedQuota) {
    return {
      unlimitedQuota: true,
      productQuota: null
    };
  }

  const rawQuotaValue = quota?.value;
  if (!hasNonEmptyValue(rawQuotaValue)) {
    return {
      unlimitedQuota: false
    };
  }

  return {
    unlimitedQuota: false,
    productQuota: Number(rawQuotaValue)
  };
};

const buildBonusProductsFields = ({
  base,
  productId,
  productDisplay,
  fetchedProduct,
  languages
}) => {
  const details = fetchedProduct?.details || {};
  const displayData = productDisplay?.[productId] || {};

  const quotaFields = buildBonusProductsQuotaFields(details?.quota);
  const pointsFields = hasNonEmptyValue(details?.points)
    ? {productPoints: Number(details?.points)}
    : {};

  let productInventory = null;
  if (details?.inventory !== 'unlimited') {
    productInventory = Number(details?.inventory) || null;
  }

  return {
    ...base,
    ...pointsFields,
    ...quotaFields,
    productInventory,
    priority: fetchedProduct?.priority || 0,
    rewardText: sanitizeRewardTextForPayload(
      displayData?.rewardText || {},
      languages
    )
  };
};

/**
 * Validates reward text for all languages
 * @param {Object} rewardText - Reward text object with language codes as keys
 * @param {Array} languages - Array of language objects or codes
 * @returns {boolean} True if all languages have valid title and description
 */
export const validateRewardTextForLanguages = (rewardText, languages) => {
  if (!rewardText || languages.length === 0) return false;
  return languages.every((lang) => {
    const code = lang.code || lang;
    const text = rewardText[code] || {};
    const hasTitle =
      typeof text.title === 'string' && text.title.trim().length > 0;
    const hasDescription =
      typeof text.description === 'string' &&
      text.description.trim().length > 0;
    return hasTitle && hasDescription;
  });
};

/**
 * Gets effective languages for validation
 * @param {string} countryCode - Country code
 * @param {Array} displayContentLanguages - Display content languages from store
 * @returns {Array} Effective languages array
 */
export const getEffectiveLanguages = (countryCode, displayContentLanguages) => {
  const countryLangs = COUNTRY_LANGUAGES_CODES[countryCode] || [];
  return displayContentLanguages && displayContentLanguages.length > 0
    ? displayContentLanguages
    : countryLangs;
};

/**
 * Gets products for display based on reward type
 * @param {Object} formData - Form data object
 * @returns {Array} Products array for display
 */
export const getProductsForDisplay = (formData) => {
  const isProductType = formData?.rewardType === REWARD_TYPES.PRODUCT;
  return isProductType
    ? formData?.selectedProducts
    : formData?.selectedPepVouchersProducts || formData?.selectedProducts;
};

/**
 * Gets reward text based on reward type and product
 * @param {Object} formData - Form data object
 * @param {string} firstProductId - First product ID
 * @returns {Object|null} Reward text object or null
 */
export const getRewardText = (formData, firstProductId) => {
  // For BONUS_PRODUCTS, rewardText is per-product (in rewardProducts), not at root level
  if (formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS) {
    return null;
  }
  const productDisplayText =
    formData?.display?.productDisplay?.[firstProductId]?.rewardText || {};
  if (formData?.rewardText && Object.keys(formData.rewardText).length > 0) {
    return formData.rewardText;
  }
  return productDisplayText;
};

/**
 * Gets image URLs based on reward type
 * @param {Object} formData - Form data object
 * @returns {Array} Array of image URLs
 */
export const getImageUrls = (formData) => {
  // For BONUS_PRODUCTS, imageUrls are sent per-product in rewardProducts
  const isBonusProducts = formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  if (isBonusProducts) {
    return [];
  }

  // For PRODUCT and PEP_VOUCHER_PRODUCT, extract images from display.productDisplay
  const isProductType = formData?.rewardType === REWARD_TYPES.PRODUCT;
  const isPepVoucherProduct =
    formData?.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;
  if (isProductType || isPepVoucherProduct) {
    const productDisplay = formData?.display?.productDisplay || {};
    const allImageUrls = [];
    Object.values(productDisplay).forEach((displayData) => {
      const images = displayData?.images || [];
      images.forEach((img) => {
        if (img?.url) {
          allImageUrls.push(img.url);
        }
      });
    });
    if (allImageUrls.length > 0) {
      return allImageUrls;
    }
  }

  // For other reward types, use formData.imageUrls at root level
  if (
    Array.isArray(formData?.imageUrls) &&
    formData.imageUrls.filter(Boolean).length > 0
  ) {
    return formData.imageUrls.filter(Boolean);
  }

  return [];
};

/**
 * Builds reward products array for API payload
 * @param {Object} formData - Form data object
 * @returns {Array} Array of reward products
 */
export const buildRewardProducts = (formData, languages) => {
  const isBonusProducts = formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const productsToMap = getProductsToMap(
    formData?.rewardType,
    formData?.selectedProducts,
    formData?.selectedPepVouchersProducts
  );
  if (!Array.isArray(productsToMap)) return [];

  const productDisplay = formData?.display?.productDisplay || {};
  const fetchedProductsMap = isBonusProducts
    ? new Map((formData?.fetchedProducts || []).map((fp) => [fp.productId, fp]))
    : null;

  return productsToMap.map((p) => {
    const productId = String(p.value ?? '');
    const isDefaultImage = Boolean(
      productDisplay?.[productId]?.useDefaultImage
    );
    // For BONUS_PRODUCTS: use per-product imageUrls
    // For PRODUCT and PEP_VOUCHER_PRODUCT: imageUrls are sent at root level, not per-product
    // When defaultImage is true, imageUrls must be empty/null per API contract
    const productImageUrls =
      isBonusProducts && !isDefaultImage
        ? getProductImageUrls(productDisplay, productId)
        : null;
    const base = {
      productId,
      productName: String(p.label ?? ''),
      defaultImage: isDefaultImage,
      imageUrls: productImageUrls
    };

    // BONUS_PRODUCTS has additional fields from fetchedProducts
    if (!isBonusProducts) return base;

    const fetchedProduct = fetchedProductsMap.get(productId);
    return buildBonusProductsFields({
      base,
      productId,
      productDisplay,
      fetchedProduct,
      languages
    });
  });
};

/**
 * Builds included segment groups for API payload
 * @param {Object} formData - Form data object
 * @param {Array} segments - Segments dropdown list
 * @returns {Array} Array of included segment groups
 */
export const buildIncludedSegmentGroups = (formData, segments) => {
  const segmentsLookup = new Map(
    (segments || []).map((s) => [String(s.id ?? s.value), s.name || s.label])
  );
  if (!Array.isArray(formData?.selectedSegments)) return [];
  return formData.selectedSegments
    .filter((g) => Array.isArray(g) && g.length > 0)
    .map((groupIds) => ({
      segments: groupIds.map((id) => ({
        segmentId: String(id),
        segmentName: String(segmentsLookup.get(String(id)) || id)
      }))
    }));
};

/**
 * Builds excluded segments for API payload
 * @param {Object} formData - Form data object
 * @param {Array} segments - Segments dropdown list
 * @returns {Array} Array of excluded segments
 */
export const buildExcludedSegments = (formData, segments) => {
  if (!Array.isArray(formData?.excludedSegments)) return [];
  // Build a lookup from segments dropdown for names
  const segmentsLookup = new Map(
    (segments || []).map((s) => [String(s.id ?? s.value), s.name || s.label])
  );
  return formData.excludedSegments.map((s) => {
    // s can be either a string ID or an object with value/id
    const segmentId = String(s?.value ?? s?.id ?? s ?? '');
    const segmentName = segmentsLookup.get(segmentId) || segmentId;
    return {
      segmentId,
      segmentName: String(segmentName)
    };
  });
};

/**
 * Checks if form data has segment errors
 * @param {Object} formData - Form data object
 * @returns {boolean} True if there are segment errors
 */
export const hasSegmentErrors = (formData) => {
  // If allCustomers is false, at least one segment group must be selected
  if (!formData?.allCustomers) {
    const hasSelectedSegments =
      formData?.selectedSegments?.length > 0 &&
      formData?.selectedSegments?.some((group) => group?.length > 0);
    if (!hasSelectedSegments) {
      return true;
    }
  }
  // If excludeSegments is true, at least one excluded segment must be selected
  if (formData?.segmentExclude) {
    const hasExcludedSegments = formData?.excludedSegments?.length > 0;
    if (!hasExcludedSegments) {
      return true;
    }
  }
  return false;
};

/**
 * Gets step 3 validation errors
 * @param {Object} formData - Form data object
 * @param {Object} validators - Validation functions object
 * @returns {Object} Object containing all step 3 errors and validation flags
 */
export const getStep3Errors = (formData, validators) => {
  const {
    validateStartDate,
    validateEndDate,
    validateQuota,
    validateQuotaDigitalCard,
    validateQuotaPhysicalCard,
    validateMaxQtyPerOrder
  } = validators;

  const isActiveReward =
    formData?.status?.toUpperCase?.() === REWARD_STATUS.ACTIVE;
  const startDateError = isActiveReward
    ? undefined
    : validateStartDate(formData.startDateDisplay, formData.endDateDisplay);
  const endDateError = validateEndDate(
    formData.endDateDisplay,
    formData.startDateDisplay
  );

  const inventoryRewardType = isInventoryManagedRewardType(
    formData?.rewardType,
    formData?.cardType
  );
  const isVisaCardBoth =
    formData?.rewardType === REWARD_TYPES.VISA_CARD &&
    formData?.cardType === REWARD_CARD_TYPES.Both;

  // For VISA_CARD with cardType Both, validate quotaDigitalCard and quotaPhysicalCard
  const shouldValidateVisaCardBothQuota =
    isVisaCardBoth && !formData.unlimitedInventory;
  const quotaDigitalCardError = shouldValidateVisaCardBothQuota
    ? validateQuotaDigitalCard(formData.quotaDigitalCard)
    : undefined;
  const quotaPhysicalCardError = shouldValidateVisaCardBothQuota
    ? validateQuotaPhysicalCard(formData.quotaPhysicalCard)
    : undefined;

  // For other inventory-managed types, validate single quota field
  const shouldValidateQuota =
    inventoryRewardType && !formData.unlimitedInventory;
  const quotaError = shouldValidateQuota
    ? validateQuota(formData.quota)
    : undefined;

  const shouldValidateMaxQtyPerOrder =
    formData.rewardType === REWARD_TYPES.PEP_VOUCHER_DISCOUNT ||
    formData.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;
  const maxQtyPerOrderError = shouldValidateMaxQtyPerOrder
    ? validateMaxQtyPerOrder(formData.maxQtyPerOrder)
    : undefined;

  // For BONUS_PRODUCTS, alertDisplayCount must be > 0 (moved from Display to Attributes tab)
  const shouldValidateAlertDisplayCount =
    formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const alertDisplayCountError =
    shouldValidateAlertDisplayCount &&
    (formData?.alertDisplayCount === undefined ||
      formData?.alertDisplayCount === null ||
      formData?.alertDisplayCount === '' ||
      Number(formData?.alertDisplayCount) <= 0)
      ? 'Alert Display Count must be greater than 0'
      : undefined;

  return {
    startDateError,
    endDateError,
    quotaError,
    quotaDigitalCardError,
    quotaPhysicalCardError,
    maxQtyPerOrderError,
    shouldValidateQuota,
    shouldValidateVisaCardBothQuota,
    shouldValidateMaxQtyPerOrder,
    alertDisplayCountError,
    shouldValidateAlertDisplayCount
  };
};

/**
 * Gets step 2 validation result
 * @param {Object} formData - Form data object
 * @param {string} countryCode - Country code
 * @param {Array} displayContentLanguages - Display content languages
 * @param {Object} displayApi - Display API object with completedCount
 * @returns {Object} Validation result object
 */
export const getStep2ValidationResult = (
  formData,
  countryCode,
  displayContentLanguages,
  displayApi
) => {
  const languages = getEffectiveLanguages(countryCode, displayContentLanguages);

  // Single image upload reward types
  const isSingleImageType = [
    REWARD_TYPES.VISA_CARD,
    REWARD_TYPES.CASH,
    REWARD_TYPES.PHYSICAL,
    REWARD_TYPES.DISCOUNT,
    REWARD_TYPES.DIGITAL,
    REWARD_TYPES.DIGITAL_PRE_ORDER,
    REWARD_TYPES.LOYALTY,
    REWARD_TYPES.PEP_VOUCHER_DISCOUNT
  ].includes(formData.rewardType);

  if (isSingleImageType) {
    const imageUrl = formData?.imageUrls?.[0];
    const hasImage = typeof imageUrl === 'string' && imageUrl.trim().length > 0;
    const allLanguagesValid = validateRewardTextForLanguages(
      formData?.rewardText,
      languages
    );
    return {
      isValid: hasImage && allLanguagesValid,
      hasImage,
      allLanguagesValid
    };
  }

  // Product-based reward types (BONUS_PRODUCTS, PRODUCT, PEP_VOUCHER_PRODUCT)
  const isPepVoucherProduct =
    formData.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;
  const selectedProducts = isPepVoucherProduct
    ? formData?.selectedPepVouchersProducts || []
    : formData?.selectedProducts || [];
  const ids = selectedProducts.map((p) => p.value);

  if (ids.length === 0) {
    return {isValid: true, noProducts: true};
  }

  const allProductsCompleted =
    (displayApi?.completedCount?.count || 0) === ids.length;

  if (languages.length === 0) {
    return {isValid: false, noLanguages: true};
  }

  const isBonusProducts = formData.rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  if (isBonusProducts) {
    const productDisplay = formData?.display?.productDisplay || {};
    const allProductsTextOk = ids.every((productId) =>
      validateRewardTextForLanguages(
        productDisplay[productId]?.rewardText,
        languages
      )
    );
    return {
      isValid: allProductsCompleted && allProductsTextOk,
      allProductsCompleted,
      allProductsTextOk
    };
  }

  // PRODUCT and PEP_VOUCHER_PRODUCT use global reward text
  const allLanguagesValid = validateRewardTextForLanguages(
    formData?.rewardText,
    languages
  );
  return {
    isValid: allProductsCompleted && allLanguagesValid,
    allProductsCompleted,
    allLanguagesValid
  };
};

/**
 * Gets reward type validation result
 * @param {Object} formData - Form data object
 * @param {string} nameError - Name validation error
 * @param {boolean} rewardTypeValid - Whether reward type is valid
 * @param {Object} validators - Validation functions object
 * @returns {Object} Object with isValid boolean and errors object
 */
export const getRewardTypeValidation = (
  formData,
  nameError,
  rewardTypeValid,
  validators
) => {
  const {
    validateOrderQuantity,
    validateBonusProductLimit,
    validateFetchedProductsDetails,
    validateReedemPoints,
    validateRewardCashAmount,
    validateRewardCardType,
    validatePoints,
    validateValue,
    validateDigitalValue,
    validateDigitalSupplier,
    validateLoyaltyURL
  } = validators;

  const validatorsMap = {
    BonusProducts: () => {
      const orderQtyError = validateOrderQuantity(formData.orderQty);
      const limitOfProductQtyError = validateBonusProductLimit(
        formData.limitOfProductQty
      );
      const selectedProductsValid = formData.selectedProducts?.length > 0;
      const fetchedProductsDetailsValid = validateFetchedProductsDetails(
        formData.fetchedProducts
      );
      return {
        isValid:
          !nameError &&
          !orderQtyError &&
          !limitOfProductQtyError &&
          selectedProductsValid &&
          fetchedProductsDetailsValid &&
          rewardTypeValid,
        errors: {
          orderQty: orderQtyError,
          limitOfProductQty: limitOfProductQtyError,
          selectedProducts: !selectedProductsValid
        }
      };
    },
    VisaCard: () => {
      const redeemPointsError = validateReedemPoints(formData.redeemPoints);
      const rewardCashAmountError = validateRewardCashAmount(
        formData.rewardCashAmount
      );
      const cardTypeError = validateRewardCardType(formData.cardType);
      return {
        isValid:
          !nameError &&
          !redeemPointsError &&
          !rewardCashAmountError &&
          !cardTypeError &&
          rewardTypeValid,
        errors: {
          redeemPoints: redeemPointsError,
          rewardCashAmount: rewardCashAmountError,
          cardType: cardTypeError
        }
      };
    },
    PepVoucherProduct: () => {
      const redeemPointsError = validatePoints(formData.redeemPoints);
      const isProductType = formData.rewardType === REWARD_TYPES.PRODUCT;
      const selectedProducts = isProductType
        ? formData.selectedProducts
        : formData.selectedPepVouchersProducts;
      const selectedProductsValid = selectedProducts?.length > 0;
      return {
        isValid:
          !nameError &&
          !redeemPointsError &&
          selectedProductsValid &&
          rewardTypeValid,
        errors: {
          redeemPoints: redeemPointsError,
          selectedProducts: !selectedProductsValid,
          validationKey: isProductType
            ? 'selectedProducts'
            : 'selectedPepVouchersProducts'
        }
      };
    },
    CashDiscount: () => {
      const redeemPointsError = validatePoints(formData.redeemPoints);
      const valueError = validateValue(formData.value);
      return {
        isValid:
          !nameError && !redeemPointsError && !valueError && rewardTypeValid,
        errors: {
          redeemPoints: redeemPointsError,
          value: valueError
        }
      };
    },
    DigitalCode: () => {
      const digitalCodeAmountError = validateDigitalValue(
        formData.digitalCodeAmount
      );
      const redeemPointsError = validateReedemPoints(formData.redeemPoints);
      const digitalCodePartnerError = validateDigitalSupplier(
        formData.digitalCodePartner
      );
      return {
        isValid:
          !nameError &&
          !digitalCodeAmountError &&
          !redeemPointsError &&
          !digitalCodePartnerError &&
          rewardTypeValid,
        errors: {
          digitalCodeAmount: digitalCodeAmountError,
          redeemPoints: redeemPointsError,
          digitalCodePartner: digitalCodePartnerError
        }
      };
    },
    Loyalty: () => {
      const redeemPointsError = validatePoints(formData.redeemPoints);
      const loyaltyUrlError = validateLoyaltyURL(formData.loyaltyUrl);
      return {
        isValid:
          !nameError &&
          !redeemPointsError &&
          !loyaltyUrlError &&
          rewardTypeValid,
        errors: {
          redeemPoints: redeemPointsError,
          loyaltyUrl: loyaltyUrlError
        }
      };
    },
    Default: () => {
      const redeemPointsError = validatePoints(formData.redeemPoints);
      return {
        isValid: !nameError && !redeemPointsError && rewardTypeValid,
        errors: {
          redeemPoints: redeemPointsError
        }
      };
    }
  };

  const validatorKey =
    REWARD_TYPE_VALIDATOR_MAP[formData.rewardType] || 'Default';
  return validatorsMap[validatorKey]();
};
